import { WavStreamPlayer } from 'wavtools';

/**
 * AEC-routed `WavStreamPlayer`.
 *
 * The default `WavStreamPlayer` plays through `AudioContext.destination`
 * (raw WebAudio). Chrome's `getUserMedia` AEC needs the far-end audio
 * to flow through an `<audio>` element whose `srcObject` is a
 * `MediaStream` - that's the signal it uses as the AEC reference.
 * Without that, the AI's voice leaks back into the mic, server VAD
 * treats it as user speech, and the session falls into a feedback
 * loop (the bug we previously worked around with a half-duplex mic
 * gate).
 *
 * This subclass plumbs playback through a `MediaStreamDestination`
 * connected to a hidden `<audio autoplay>` element. The audible
 * output is identical to the parent class - but the browser now sees
 * it as "remote audio" and applies AEC, restoring full-duplex
 * conversation with proper barge-in.
 *
 * Override surface is the private `_start()` method, called lazily by
 * `add16BitPCM` the first time audio arrives. Reaching past the
 * underscore is the only way to redirect the output without forking
 * the library; if `wavtools` ever exposes a sink hook, prefer that.
 */
// @ts-expect-error - parent's `_start` is declared as a typeless
// `private _start;` field; ours is a typed method. Structurally
// different, but at runtime the override works because JS has no
// real privates.
export class AECWavStreamPlayer extends WavStreamPlayer {
  private aecSink: MediaStreamAudioDestinationNode | null = null;
  private aecAudioEl: HTMLAudioElement | null = null;

  override async connect(): Promise<true> {
    const result = await super.connect();
    const ctx = this.context as AudioContext;
    this.aecSink = ctx.createMediaStreamDestination();

    const el = document.createElement('audio');
    el.autoplay = true;
    el.srcObject = this.aecSink.stream;
    el.style.display = 'none';
    document.body.appendChild(el);
    this.aecAudioEl = el;

    return result;
  }

  /**
   * Mirror of the upstream `_start()` (wav_stream_player.js:76) with
   * one critical change: connect `streamNode` to `this.aecSink`
   * instead of `ctx.destination`. The hidden `<audio>` element plays
   * the stream and the browser uses it as the AEC reference.
   *
   * Declared `private` to match the parent's visibility (the parent
   * .d.ts marks `_start` private). At runtime the subclass method
   * shadows the base class one - JavaScript has no real "private",
   * so this just keeps TypeScript happy.
   */
  private _start(): true {
    const ctx = this.context as AudioContext;
    const sink = this.aecSink;
    if (!sink) {
      throw new Error('AECWavStreamPlayer: connect() must run before _start()');
    }
    const streamNode = new AudioWorkletNode(ctx, 'stream_processor');
    streamNode.connect(sink);
    streamNode.port.onmessage = e => {
      const { event } = e.data;
      if (event === 'stop') {
        streamNode.disconnect();
        this.stream = null;
      } else if (event === 'offset') {
        const { requestId, trackId, offset } = e.data;
        const currentTime = offset / this.sampleRate;
        // The parent .d.ts types this as `{}`; it is keyed by requestId.
        (
          this.trackSampleOffsets as Record<
            string,
            { trackId: string; offset: number; currentTime: number }
          >
        )[requestId] = { trackId, offset, currentTime };
      }
    };
    const analyser = this.analyser as AnalyserNode;
    analyser.disconnect();
    streamNode.connect(analyser);
    this.stream = streamNode;
    return true;
  }

  /**
   * Tear down the AEC sink + hidden `<audio>` element + close the
   * underlying AudioContext. The parent class has no `close()` (it
   * relies on AudioContext GC), but Chrome's GC of
   * `MediaStreamAudioDestinationNode` graphs is unreliable - without
   * an explicit teardown, opening doubt mode → closing → opening
   * again leaves the previous session's graph alive and the new
   * session's audio plays silent or crackly. We disconnect every
   * node in order (worklet → sink → analyser) and call
   * `context.close()` to release the entire graph.
   */
  cleanupAEC(): void {
    // Stop the worklet first so it releases its outputs (which
    // include the AEC sink and the analyser). `streamNode.disconnect()`
    // with no args drops every connection at once.
    if (this.stream) {
      try {
        this.stream.disconnect();
      } catch {
        /* already disconnected */
      }
      this.stream = null;
    }
    // Pause and detach the hidden <audio> first; the browser will
    // stop sourcing from the (about-to-be-closed) MediaStream.
    if (this.aecAudioEl) {
      try {
        this.aecAudioEl.pause();
      } catch {
        /* ignore */
      }
      this.aecAudioEl.srcObject = null;
      this.aecAudioEl.remove();
      this.aecAudioEl = null;
    }
    if (this.aecSink) {
      try {
        this.aecSink.disconnect();
      } catch {
        /* ignore */
      }
      this.aecSink = null;
    }
    // Close the AudioContext entirely. Without this, repeated
    // open/close cycles accumulate dead contexts (Chrome caps at ~6
    // simultaneous), eventually breaking new sessions.
    const ctx = this.context as AudioContext | undefined;
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => {
        /* ignore close failure - page reload will recover */
      });
    }
  }
}
